// Adaptive fight music on a look-ahead scheduler (audio clock, so timing
// stays tight when frames drop). Four-bar loops in the in-scale, one theme
// in three settings — each stage has its own groove, so they differ even in
// a first round, when only the drums play:
//   dojo     the theme: straight, odaiko and ka on the backbeat
//   park     a festival: swung, shime on the beat answered by ka off it
//   rooftop  night: driving "don-doko" odaiko, a low bell opening the loop
// Layers build with intensity:
//   0  odaiko pulse, shime timekeeping, ka
//   1  + koto arpeggios
//   2  + shakuhachi phrases
//   3  + shime rolls into the loop (long combos)

import type { StageId } from '@/render/stages';
import type { Sfx } from './sfx';

/** Drum patterns are one character per sixteenth, a bar per string. */
interface StageMusic {
  /** O low odaiko, o odaiko, - a light stroke — per bar. */
  odaiko: string[];
  /** X accent, x timekeeping, - ghost — every bar. */
  shime: string;
  /** x a ka stroke — every bar. */
  ka: string;
  kaGain: number;
  kaRate: number;
  /** How many sixteenths late the off-beat eighths land (0 = straight). */
  swing: number;
  /** Gain of a low temple bell opening the loop (0 = none). */
  bell: number;
  /** Koto arpeggio per bar: scale degrees (0–12), one every eighth note. */
  koto: number[][];
  kotoGain: number;
  /** Playback-rate shift for the koto (0.5 = an octave down, darker). */
  kotoRate: number;
  /** Shakuhachi note opening each bar: a degree, or null to rest. Each note
   *  rings 1.8 s ÷ rate, so a bar must stay that long at the top tempo. */
  flute: (number | null)[];
  fluteGain: number;
  fluteRate: number;
}

const STAGES: Record<StageId, StageMusic> = {
  // The theme. Calm and spacious: a slow, open arpeggio
  dojo: {
    // prettier-ignore
    odaiko: [
      'O.........o.....',
      'O.....o.o.......',
      'O.........o.....',
      'O.....o.o..o..o.',
    ],
    shime: 'X.x.x.x.X.x.x.x.',
    ka: '....x.......x...',
    kaGain: 0.45,
    kaRate: 1,
    swing: 0,
    bell: 0,
    koto: [
      [0, 2, 4, 2, 5, 4, 2, 4],
      [0, 2, 4, 5, 7, 5, 4, 2],
      [3, 5, 7, 5, 8, 7, 5, 7],
      [2, 4, 5, 7, 5, 4, 2, 0],
    ],
    kotoGain: 0.34,
    kotoRate: 1,
    flute: [7, null, 5, null],
    fluteGain: 0.32,
    fluteRate: 1,
  },
  // A festival under the cherry trees: the theme an octave up, swung, the
  // shime on the beat and a bright ka off it, a festival flute every bar
  park: {
    // prettier-ignore
    odaiko: [
      'O.......o.......',
      'O.......o.....o.',
      'O.......o.......',
      'O.......o.o.o.o.',
    ],
    shime: 'X...x...X...x...',
    ka: '..x...x...x...x.',
    kaGain: 0.3,
    kaRate: 1.25,
    swing: 0.4,
    bell: 0,
    koto: [
      [5, 7, 9, 7, 10, 9, 7, 9],
      [5, 7, 9, 10, 12, 10, 9, 7],
      [4, 5, 7, 9, 7, 5, 4, 5],
      [7, 9, 10, 12, 10, 9, 7, 5],
    ],
    kotoGain: 0.3,
    kotoRate: 1,
    flute: [9, 7, 10, null],
    fluteGain: 0.28,
    fluteRate: 1,
  },
  // Night and urgent: "don-doko" odaiko into every half bar, the shime
  // ticking sixteenths, a low koto ostinato and a low, breathy shakuhachi
  rooftop: {
    // prettier-ignore
    odaiko: [
      'O.....--o.....--',
      'O.....--o...o.--',
      'O.....--o.....--',
      'O...o...o.o.-o-o',
    ],
    shime: 'X-x-x-x-X-x-x-x-',
    ka: '....x.......x...',
    kaGain: 0.45,
    kaRate: 0.9,
    swing: 0,
    bell: 0.3,
    koto: [
      [0, 0, 3, 0, 4, 3, 0, 1],
      [0, 0, 3, 5, 4, 3, 1, 0],
      [0, 0, 3, 0, 4, 3, 0, 1],
      [5, 4, 3, 1, 3, 1, 0, 1],
    ],
    kotoGain: 0.42,
    kotoRate: 0.5,
    flute: [3, null, 1, null],
    fluteGain: 0.32,
    fluteRate: 0.5,
  },
};

const SHIME_GAIN: Record<string, number> = { X: 0.28, x: 0.14, '-': 0.07 };
/** The 1320 Hz bell down to D5, the scale's root, ringing twice as long. */
const BELL_RATE = 587.33 / 1320;

export class Music {
  private timer = 0;
  private step = 0;
  private nextAt = 0;
  private bpm = 100;
  private playing = false;
  private stage: StageMusic = STAGES.dojo;
  private intensity = 0;

  constructor(private readonly sfx: Sfx) {}

  setStage(stage: StageId): void {
    this.stage = STAGES[stage];
  }

  /** 0–3: which layers play (see the header). */
  setIntensity(level: number): void {
    this.intensity = Math.max(0, Math.min(3, Math.round(level)));
  }

  start(bpm: number): void {
    const ctx = this.sfx.context;
    if (!ctx) return;
    this.bpm = bpm;
    if (this.playing) return;
    this.playing = true;
    this.step = 0;
    this.nextAt = ctx.currentTime + 0.1;
    this.timer = window.setInterval(() => this.schedule(), 25);
  }

  setTempo(bpm: number): void {
    this.bpm = bpm;
  }

  stop(): void {
    this.playing = false;
    clearInterval(this.timer);
  }

  /** Schedule `bars` bars from `from` in one go (offline rendering / tests). */
  renderBars(bars: number, from: number, bpm = this.bpm): void {
    const stepLen = 60 / bpm / 4;
    for (let i = 0; i < bars * 16; i++) this.note(i % 64, from + i * stepLen, stepLen);
  }

  private schedule(): void {
    const ctx = this.sfx.context;
    if (!ctx || !this.playing) return;
    const stepLen = 60 / this.bpm / 4; // sixteenth notes
    // If the tab was suspended, don't try to catch up on every missed note;
    // a new context (back from the background) starts its clock at 0 again
    if (this.nextAt < ctx.currentTime - 0.2 || this.nextAt > ctx.currentTime + 1) {
      this.nextAt = ctx.currentTime + 0.05;
    }
    while (this.nextAt < ctx.currentTime + 0.12) {
      this.note(this.step % 64, this.nextAt, stepLen);
      this.nextAt += stepLen;
      this.step++;
    }
  }

  private note(step: number, at: number, stepLen: number): void {
    const bar = Math.floor(step / 16);
    const s = step % 16;
    const st = this.stage;
    // Swing: the off-beat eighth lands late, the sixteenths around it halfway
    const late = s % 4 === 2 ? 1 : s % 2 === 1 ? 0.5 : 0;
    const m = { music: true, at: at + late * st.swing * stepLen } as const;
    const play = this.sfx.play.bind(this.sfx);
    const lvl = this.intensity;

    // A low bell opens the loop
    if (st.bell && step === 0) play('bell', { ...m, gain: st.bell, rate: BELL_RATE, send: 0.6 });
    // Odaiko: the pulse, with a fill in the fourth bar
    const drum = st.odaiko[bar]![s];
    if (drum === 'O') play('taikoLow', { ...m, gain: 0.75, send: 0.3 });
    else if (drum === 'o') play('taiko', { ...m, gain: 0.5, send: 0.3 });
    else if (drum === '-') play('taiko', { ...m, gain: 0.25, send: 0.3 });
    // Ka
    if (st.ka[s] === 'x') play('ka', { ...m, gain: st.kaGain, pan: 0.2, rate: st.kaRate });
    // Shime keeps time, a touch higher off the beat
    const shime = SHIME_GAIN[st.shime[s]!];
    if (shime) {
      play('shime', { ...m, gain: shime, pan: -0.25, rate: 1 + (s % 4 === 0 ? 0 : 0.04) });
    }
    // Rolls into the loop at full intensity
    if (lvl >= 3 && bar === 3 && s >= 12)
      play('shime', { ...m, gain: 0.18 + (s - 12) * 0.05, pan: -0.25 });

    // Koto arpeggio on the eighths
    if (lvl >= 1 && s % 2 === 0) {
      const deg = st.koto[bar]![s / 2]!;
      play(`koto${deg}`, {
        ...m,
        gain: st.kotoGain * (s % 8 === 0 ? 1 : 0.75),
        rate: st.kotoRate,
        pan: 0.3 - (s / 16) * 0.6,
        send: 0.35,
      });
    }
    // Shakuhachi: a long note opening a bar
    const deg = st.flute[bar];
    if (lvl >= 2 && s === 0 && deg != null) {
      play(`flute${deg}`, { ...m, gain: st.fluteGain, pan: 0.15, send: 0.6, rate: st.fluteRate });
    }
  }
}
